import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { fmtIDR } from "@/lib/format";
import { Search } from "lucide-react";

export function ItemSearch({ items, value, onSelect, testId, placeholder = "Ketik SKU / brand / nama barang..." }) {
  const selected = items.find((i) => i.id === value);
  const [q, setQ] = useState("");
  const [openList, setOpenList] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpenList(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const results = useMemo(() => {
    const words = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
    const scored = items
      .map((i) => {
        const hay = `${i.sku} ${i.brand} ${i.name} ${i.variant || ""} ${i.color || ""} ${i.size || ""}`.toLowerCase();
        if (!words.every((w) => hay.includes(w))) return null;
        const starts = words.some((w) => i.sku?.toLowerCase().startsWith(w) || i.name?.toLowerCase().startsWith(w) || i.brand?.toLowerCase().startsWith(w));
        return { i, score: (starts ? 0 : 1) + (i.sell_price > 0 ? 0 : 2) };
      })
      .filter(Boolean)
      .sort((a, b) => a.score - b.score || a.i.name.localeCompare(b.i.name))
      .slice(0, 12);
    return scored.map((s) => s.i);
  }, [items, q]);

  const pick = (i) => { onSelect(i.id); setQ(""); setOpenList(false); };
  const onKey = (e) => {
    if (!openList && ["ArrowDown", "Enter"].includes(e.key)) { setOpenList(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (results[hi]) pick(results[hi]); }
    else if (e.key === "Escape") setOpenList(false);
  };

  return (
    <div ref={boxRef} className="relative flex-1 min-w-0">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input data-testid={testId} value={openList || !selected ? q : `${selected.sku ? selected.sku + " · " : ""}${selected.name}`}
          onChange={(e) => { setQ(e.target.value); setOpenList(true); setHi(0); }} onFocus={() => { setOpenList(true); setHi(0); }} onKeyDown={onKey}
          placeholder={selected ? "" : placeholder} autoComplete="off" className={`pl-8 ${selected && !openList ? "font-medium text-slate-900" : ""}`} />
      </div>
      {openList && (
        <div data-testid={`${testId}-results`} className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-md border bg-white shadow-lg text-sm">
          {results.length === 0 && <div className="px-3 py-2 text-slate-400 text-xs">Tidak ada barang cocok "{q}"</div>}
          {results.map((i, idx) => (
            <button type="button" key={i.id} data-testid={`${testId}-opt-${i.sku || i.id.slice(0, 6)}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(i)} onMouseEnter={() => setHi(idx)}
              className={`w-full text-left px-3 py-1.5 flex items-center gap-2 ${idx === hi ? "bg-sky-50" : ""} ${i.sell_price > 0 ? "" : "opacity-60"}`}>
              {i.sku && <span className="font-mono text-[10px] text-slate-500 shrink-0">{i.sku}</span>}
              <span className="truncate"><span className="text-slate-500">{i.brand}</span> <span className="font-medium text-slate-900">{i.name}</span>{(i.variant || i.size) && <span className="text-slate-400 text-xs"> {[i.variant, i.color, i.size].filter(Boolean).join(" · ")}</span>}</span>
              <span className="ml-auto font-mono text-xs shrink-0">{i.sell_price > 0 ? fmtIDR(i.sell_price) : <span className="text-amber-600">belum ada harga</span>}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
